import * as React from 'react';
import { ApolloClient, InMemoryCache, ApolloLink, HttpLink, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Kind, OperationTypeNode, GraphQLScalarType } from 'graphql';
import { withScalars } from 'apollo-link-scalars';
import { makeExecutableSchema } from '@graphql-tools/schema';
import { RetryLink } from '@apollo/client/link/retry';
import { curatedTypeDefs } from '../code-generated/curatedSchemaTypeDefs.generated';
import { dataPlaneTypeDefs } from '../code-generated/dataPlaneSchemaTypeDefs.generated';
import { controlPlaneTypeDefs } from '../code-generated/controlPlaneSchemaTypeDefs.generated';
import { useAppSettings } from './AppSettings';
import { useInstrumentation } from './Instrumentation';
import { useMidwayToken } from './Midway';
import { useAddNotification } from './Notifications';
import { TimeoutLink } from '../utils/TimeoutLink';
import { DataProductApi } from '../constants/api';
import { useDataProductEndpoints } from '../hooks/useDataProductEndpoints';

type GsocApolloClients = Record<DataProductApi, ApolloClient<object>>;

const AWSDateTime = new GraphQLScalarType({
  name: 'AWSDateTime',
  description: 'An ISO-8601 encoded UTC datetime string.',
  serialize: value => (value as Date).toISOString(),
  parseValue: value => new Date(value as string | number),
  parseLiteral: ast => (ast.kind === Kind.STRING || ast.kind === Kind.INT ? new Date(ast.value) : null),
});

const AWSJSON = new GraphQLScalarType({
  name: 'AWSJSON',
  description: 'An untyped json blob',
  serialize: value => JSON.stringify(value),
  parseValue: value => (typeof value === 'string' ? (JSON.parse(value) as unknown) : null),
  parseLiteral: ast => (ast.kind === Kind.STRING ? (JSON.parse(ast.value) as unknown) : null),
});

const Long = new GraphQLScalarType({
  name: 'Long',
  description: 'A 64-bit integer value',
  serialize: value => Number(value as number | string),
  parseValue: value => Number(value as number | string),
  parseLiteral: ast => (ast.kind === Kind.INT || ast.kind === Kind.STRING ? Number(ast.value) : null),
});

// Pre-build per-API scalars links at module level to avoid expensive schema parsing during render
const scalarsLinkForApi: Record<DataProductApi, ReturnType<typeof withScalars>> = {
  [DataProductApi.CURATED_API]: withScalars({
    schema: makeExecutableSchema({ typeDefs: curatedTypeDefs, resolvers: { AWSDateTime, AWSJSON, Long } }),
  }),
  [DataProductApi.DATA_PLANE_API]: withScalars({
    schema: makeExecutableSchema({ typeDefs: dataPlaneTypeDefs, resolvers: { AWSDateTime } }),
  }),
  [DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API]: withScalars({
    schema: makeExecutableSchema({ typeDefs: controlPlaneTypeDefs, resolvers: { AWSDateTime } }),
  }),
};

const GsocApolloContext = React.createContext<GsocApolloClients | null>(null);

/**
 * Returns the Apollo Client for the given API. Defaults to CURATED_API if not specified.
 * Returns undefined if no GsocApolloProvider is in the tree (e.g., in tests using MockedProvider).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useGsocApolloClient = (api?: DataProductApi): ApolloClient<object> | undefined => {
  const clients = React.useContext(GsocApolloContext);
  if (!clients) return undefined;
  return clients[api ?? DataProductApi.CURATED_API];
};

export function GsocApolloProvider(props: { readonly children: React.ReactNode }) {
  const { graphqlApiUrl } = useAppSettings();
  const apiEndpointConfig = useDataProductEndpoints();
  const midwayToken = useMidwayToken();
  const addNotification = useAddNotification();
  const { rumClient } = useInstrumentation();

  const errorLink = React.useMemo(
    () =>
      onError(({ graphQLErrors, networkError, operation }) => {
        if (networkError) {
          if ('statusCode' in networkError && networkError.statusCode === 401) {
            return;
          }
          rumClient?.recordError(networkError);
          console.error(`[Network error]: ${networkError.message}`, networkError);
          addNotification({
            header: 'Network error',
            message: networkError.message,
            type: 'error',
          });
        } else {
          for (const graphqlError of graphQLErrors ?? []) {
            rumClient?.recordError(graphqlError);
            console.error(
              `[GraphQL error]: Operation: ${operation.operationName}, Message: ${graphqlError.message}, Location: ${String(graphqlError.locations)}, Path: ${String(graphqlError.path)}`,
            );
            addNotification({
              header: 'GraphQL error',
              message: graphqlError.message,
              type: 'error',
            });
          }
        }
      }),
    [rumClient, addNotification],
  );

  const unauthorizedHandlerLink = React.useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        const result = forward(operation);
        return result.map(({ data, errors }) => {
          const filteredErrors =
            errors?.filter(error => (error as unknown as { errorType?: string }).errorType !== 'Unauthorized') ?? [];
          return {
            data,
            ...(filteredErrors.length > 0 ? { errors: filteredErrors } : {}),
          };
        });
      }),
    [],
  );

  const retryLink = React.useMemo(
    () =>
      new RetryLink({
        delay: { initial: 300, max: Infinity, jitter: true },
        attempts: {
          max: 5,
          retryIf: (networkError: object, _operation) => {
            if (isNetworkErrorWithStatusCode(networkError)) {
              return [408, 435, 429, 502, 503, 504].includes(networkError.statusCode);
            }
            return true;
          },
        },
      }),
    [],
  );

  const timeoutLink = React.useMemo(() => new TimeoutLink({ timeoutMs: 8000 }), []);

  const sharedLinks = React.useMemo(
    () => ApolloLink.from([errorLink, unauthorizedHandlerLink, retryLink, timeoutLink]),
    [errorLink, unauthorizedHandlerLink, retryLink, timeoutLink],
  );

  const createHttpLinkForApi = React.useCallback(
    (api: DataProductApi) =>
      new HttpLink({
        uri: apiEndpointConfig[api],
        headers: { Authorization: `Bearer ${midwayToken}` },
      }),
    [midwayToken, apiEndpointConfig],
  );

  const wsLink = React.useMemo(
    () =>
      new GraphQLWsLink(
        createClient({
          url: graphqlApiUrl,
          connectionParams: { headers: { Authorization: `Bearer ${midwayToken}` } },
        }),
      ),
    [midwayToken, graphqlApiUrl],
  );

  const createLinkForApi = React.useCallback(
    (api: DataProductApi) => {
      const httpLink = createHttpLinkForApi(api);
      // Only the curated API uses subscriptions currently
      if (api === DataProductApi.CURATED_API) {
        const splitLink = split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION
            );
          },
          wsLink,
          httpLink,
        );
        return ApolloLink.from([scalarsLinkForApi[api], sharedLinks, splitLink]);
      }
      return ApolloLink.from([scalarsLinkForApi[api], sharedLinks, httpLink]);
    },
    [sharedLinks, createHttpLinkForApi, wsLink],
  );

  const clients = React.useMemo<GsocApolloClients>(
    () => ({
      [DataProductApi.CURATED_API]: new ApolloClient({
        cache: new InMemoryCache({
          typePolicies: {
            GsocContactV2: { keyFields: ['contactId'] },
            GsocDataflowV2: {
              keyFields: ['dataflowId', 'destination', ['config', ['id']], 'source', ['config', ['id']]],
            },
            GsocCapabilityV2: { keyFields: ['id'] },
          },
        }),
        link: createLinkForApi(DataProductApi.CURATED_API),
        connectToDevTools: true,
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
      [DataProductApi.DATA_PLANE_API]: new ApolloClient({
        cache: new InMemoryCache(),
        link: createLinkForApi(DataProductApi.DATA_PLANE_API),
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
      [DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API]: new ApolloClient({
        cache: new InMemoryCache(),
        link: createLinkForApi(DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API),
        defaultOptions: { query: { errorPolicy: 'all', returnPartialData: true } },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Update links when midway token or endpoints change
  React.useEffect(() => {
    for (const api of Object.values(DataProductApi)) {
      clients[api].setLink(createLinkForApi(api));
    }
  }, [clients, createLinkForApi]);

  // GC all caches periodically
  React.useEffect(() => {
    const intervalHandle = setInterval(
      () => {
        for (const api of Object.values(DataProductApi)) {
          clients[api].cache.gc();
        }
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(intervalHandle);
  }, [clients]);

  return <GsocApolloContext.Provider value={clients}>{props.children}</GsocApolloContext.Provider>;
}

function isNetworkErrorWithStatusCode(err: object): err is { statusCode: number } {
  return 'statusCode' in err;
}
